import {
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  FormControl,
  IconButton,
  InputBase as InputBaseMui,
  useTheme,
  styled,
  Paper as PaperMui,
  ClickAwayListener,
  Box,
  alpha,
  InputBaseProps,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { Suggestion } from "@interfaces";
import { scrollToTop } from "@components";
import { SearchSuggestions } from ".";

export const SearchBarContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  zIndex: 1,
  width: "100%",
  justifyContent: "center",
  maxHeight: "40px",
  height: "40px",
}));

const PaperForm = styled(PaperMui)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 20,
  overflow: "hidden",
  width: "100%",
  maxWidth: "620px",
  position: "absolute",
  minHeight: "48px",
  border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
  "&:focus-within": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  "&:focus": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  "&:active": {
    border: `2px solid ${theme.palette.primary.main}`,
  },
}));

const InputBase = styled(InputBaseMui)(({ theme }) => ({
  height: "48px",
  fontSize: "16px",
  paddingRight: 0,
  marginRight: "8px",
  marginLeft: "8px",
  "& input": {
    height: "35px",
    padding: "9px",
  },
  color: theme.palette.text.gray,
}));

interface SearchHeaderProps extends InputBaseProps {
  className?: string;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  search?: () => Promise<void>;
  onPreliminaryItemClick?: (item: Suggestion) => void;
  preliminaryResults?: Suggestion[];
  addManuallyItemText?: string;
  showSuggestions?: boolean;
  locationState?: any;
  showAddManuallyItemButton?: boolean;
  showEmptyResultsMessage?: boolean;
  showErrorResultsMessage?: boolean;
  showLoader?: boolean;
  obscureInTracker?: boolean;
}

export const SearchBar = ({
  className,
  id = "search-bar-input",
  searchText,
  setSearchText,
  onPreliminaryItemClick = () => {},
  search = () => Promise.resolve(),
  preliminaryResults = [],
  addManuallyItemText,
  showSuggestions = true,
  locationState,
  showAddManuallyItemButton,
  showEmptyResultsMessage,
  showErrorResultsMessage,
  showLoader,
  obscureInTracker,
  ...props
}: SearchHeaderProps) => {
  const theme = useTheme();

  const [focused, setFocused] = useState<boolean>(false);

  const handleChange = useCallback(
    (setter: Dispatch<SetStateAction<string>>) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
      },
    []
  );

  const handleEmptySearch = useCallback(() => {
    setSearchText("");
    scrollToTop();
  }, [setSearchText]);

  const handleDelete = useCallback(() => {
    setSearchText("");
    scrollToTop();
  }, [setSearchText]);

  useEffect(() => {
    if (search) {
      void search();
    }
  }, [search]);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setFocused(false);
      }}
    >
      <PaperForm elevation={0} className={className}>
        <FormControl fullWidth>
          <InputBase
            id={id}
            autoComplete="off"
            value={searchText}
            onChange={handleChange(setSearchText)}
            onFocus={() => {
              setFocused(true);
            }}
            endAdornment={
              searchText ? (
                <IconButton onClick={() => handleDelete()}>
                  <Close style={{ color: theme.palette.text.black }} />
                </IconButton>
              ) : (
                <IconButton onClick={() => handleEmptySearch()}>
                  <Search style={{ color: theme.palette.text.black }} />
                </IconButton>
              )
            }
            inputProps={{
              ...props.inputProps,
              ...(obscureInTracker && { "data-openreplay-obscured": "" }),
            }}
            {...props}
          />
          {showSuggestions && (
            <SearchSuggestions
              focused={focused}
              setFocused={setFocused}
              searchText={searchText}
              preliminarySearchResults={preliminaryResults}
              onItemClick={onPreliminaryItemClick}
              locationState={locationState}
              addManuallyItemText={addManuallyItemText}
              showAddManuallyItemButton={showAddManuallyItemButton}
              showEmptyResultsMessage={showEmptyResultsMessage}
              showErrorResultsMessage={showErrorResultsMessage}
              showLoader={showLoader}
            />
          )}
        </FormControl>
      </PaperForm>
    </ClickAwayListener>
  );
};
